<script lang="ts" setup>
import { ref, onMounted } from 'vue';
import { useSalesRepStore } from '@/store';
import { SalesRepInterface, PaginatedSalesRepParams } from '@/types';
import AddSalesRep from './AddSalesRep';

type PageEvent = {
  first: number;
  rows: number;
  page: number;
  pageCount: number;
  sortField: string | ((item: Record<string, unknown>) => string) | undefined;
  sortOrder: 0 | 1 | -1 | null | undefined;
  filters: object;
  originalEvent: object;
};

const salesRepStore = useSalesRepStore();
const showAddSalesRep = ref(false);
const params = ref<PaginatedSalesRepParams>({
  page: 1,
  per_page: 10,
  active: null,
});
const activeOptions = [
  { label: 'All', value: null },
  { label: 'Active', value: true },
  { label: 'Inactive', value: false },
];

function openAddSalesRepModal() {
  showAddSalesRep.value = true;
}

function closeAddSalesRepModal() {
  showAddSalesRep.value = false;
}

async function init() {
  await salesRepStore.fetchPaginatedSalesReps(params.value);
}

onMounted(async () => {
  await init();
});

function createMenuModel(salesRep: SalesRepInterface) {
  return [
    {
      label: salesRep.active === 1 ? 'Deactivate' : 'Activate',
      command: async () => {
        await toggleSalesRepStatus(salesRep);
      },
    },
  ];
}

async function toggleSalesRepStatus(salesRep: SalesRepInterface) {
  const newStatus = salesRep.active ? 0 : 1;
  let updateSucceeded = true;

  await salesRepStore
    .updateSalesRep(salesRep.id, { active: newStatus })
    .catch((error: { status: number; data: { message: string } }) => {
      updateSucceeded = false;
      if (error.status === 422) {
        salesRepStore.doToast(`Failed to save Sales Rep: ${error.data.message}`, '', true);
      } else {
        salesRepStore.doToast(`An unknown error occurred`, '', true);
      }
    });

  if (updateSucceeded) {
    salesRepStore.doToast(`Sales Rep ${newStatus ? 'activated' : 'deactivated'} successfully`);
  }

  await salesRepStore.fetchPaginatedSalesReps(params.value);
}

async function onPageChange(event: PageEvent) {
  params.value.page = Math.floor(event.first / event.rows) + 1;
  params.value.per_page = event.rows;
  await init();
}

async function onFilterChange() {
  params.value.page = 1;
  await init();
}
</script>

<template>
  <Card>
    <template #header>
      <div class="flex justify-between items-center">
        <h2 class="text-lg font-semibold">Sales Representatives</h2>
      </div>
    </template>

    <template #content>
      <div class="flex flex-wrap">
        <div class="flex flex-1">
          <div class="flex items-center justify-between mb-2">
            <span class="font-medium pt-2 mr-1">Filter by status:</span>
            <Dropdown
              v-model="params.active"
              :options="activeOptions"
              option-label="label"
              option-value="value"
              class="w-48"
              placeholder="Select Status"
              @change="onFilterChange"
            />
          </div>
        </div>
        <AppButton text="Add Sales Rep" type="primary" @click="openAddSalesRepModal" />
      </div>
      <AddSalesRep
        v-if="showAddSalesRep"
        @cancel="closeAddSalesRepModal"
        @refresh="init"
        @save="
          async () => {
            closeAddSalesRepModal();
            await init();
          }
        "
      />
      <DataTable
        :value="salesRepStore.paginatedSalesReps"
        :total-records="salesRepStore.totalRecords"
        :lazy="true"
        :paginator="true"
        :rows="params.per_page ?? 10"
        :first="((params.page ?? 1) - 1) * (params.per_page ?? 10)"
        :loading="salesRepStore.loading"
        data-key="id"
        responsive-layout="scroll"
        paginator-template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        current-page-report-template="Showing {first} to {last} of {totalRecords}"
        :rows-per-page-options="[10, 20, 30]"
        loading-icon="logo-spinner"
        @page="onPageChange"
      >
        <Column field="display_name" header="Name" />
        <Column field="active" header="Active">
          <template #body="{ data }">
            {{ data.active === 1 ? 'Yes' : 'No' }}
          </template>
        </Column>
        <Column field="actions" header="Actions" header-style="width: 4em" class="text-center">
          <template #body="{ data }">
            <ActionMenu :menu-items="createMenuModel(data)" icon="more_horiz" />
          </template>
        </Column>
      </DataTable>
    </template>
  </Card>
</template>
